import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Loader2 } from 'lucide-react';

/**
 * Wraps a save action with a version description prompt.
 * Usage: <SaveWithVersionDialog open={open} onOpenChange={setOpen} onSave={async (desc) => { ... }} saving={saving} title="Save Report" />
 */
export function SaveWithVersionDialog({ open, onOpenChange, onSave, saving, title = 'Save Changes', nextVersion = null }) {
  const [description, setDescription] = useState('');

  const handleSave = () => {
    onSave(description.trim());
    setDescription('');
  };

  return (
    <Dialog open={open} onOpenChange={v => { onOpenChange(v); if (!v) setDescription(''); }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {title}
            {nextVersion != null && (
              <span className="text-xs font-mono font-normal px-1.5 py-0.5 rounded bg-primary/15 text-primary" data-testid="save-next-version">Saving as v{nextVersion}</span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Add an optional commit message describing what changed in this version.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs text-muted-foreground">What changed?</Label>
            <Input
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="e.g. Added new chart, fixed filter, updated formula..."
              className="rounded-lg bg-muted/50 border-border/50 mt-1"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter' && !saving) handleSave(); }}
              data-testid="version-description-input"
            />
            <p className="text-[10px] text-muted-foreground/70 mt-1">A version snapshot will be saved automatically</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 rounded-lg">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="confirm-save-version-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save & Version
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
